/**
 * Which product/variant the page is showing. Colour "variants" are separate
 * products, so this changes without a page load.
 *
 *   Publish    window.duskPublishProductContext({ productId, variantId, handle, url })
 *   Read now   window.DUSK.productContext
 *   Subscribe  document.addEventListener('dusk:product:changed', (e) => e.detail)
 *
 * window.DUSK.productContext is retained, not just broadcast — Aurora's
 * islands mount lazily on scroll and would otherwise miss a swap that
 * already happened. See ADR 0005 in the dusk-aurora repo for the contract.
 *
 * Pattern for any future window.DUSK.* publisher (e.g. a PDP gallery/
 * accordion/finance-widget context): keep the dedup/merge baseline in a
 * private variable (`current` below), and only ever WRITE a copy to
 * window.DUSK.* — never read your baseline back from the public field.
 * window.DUSK is a shared, publicly-writable object; anything else that
 * ever touches window.DUSK.productContext directly (another script,
 * a future Aurora write, a well-meaning dev who doesn't know this
 * function exists) would otherwise corrupt what this module compares
 * against on the next publish. Keeping the baseline private makes such
 * a stray write inert instead of corrupting.
 */
(function () {
  const EVENT_NAME = 'dusk:product:changed';
  const SEED_SELECTOR = '#dusk-product-context';
  const KEYS = ['productId', 'variantId', 'handle', 'url'];

  // Private baseline — do not read this back from window.DUSK.productContext.
  // See the file doc comment above.
  let current = {};

  // Liquid emits numbers, the DOM emits strings — normalise so change detection
  // doesn't report a difference that isn't one.
  function normalise(input) {
    const next = {};
    if (!input) return next;

    KEYS.forEach((key) => {
      const value = input[key];
      if (value === null || value === undefined || value === '') return;
      next[key] = String(value);
    });

    return next;
  }

  function parseSeed(root) {
    const element = (root || document).querySelector(SEED_SELECTOR);
    if (!element || !element.textContent.trim()) return null;

    try {
      return normalise(JSON.parse(element.textContent));
    } catch (error) {
      console.warn('[product-context] Could not parse seed JSON:', error);
      return null;
    }
  }

  function publish(partial) {
    const incoming = normalise(partial);
    if (!Object.keys(incoming).length) return;

    // A new product replaces rather than merges, so the old variantId can't linger.
    const isDifferentProduct = incoming.productId && incoming.productId !== current.productId;
    const next = isDifferentProduct ? incoming : Object.assign({}, current, incoming);

    if (KEYS.every((key) => next[key] === current[key])) return;

    current = next;
    window.DUSK = window.DUSK || {};
    window.DUSK.productContext = Object.assign({}, current);
    document.dispatchEvent(
      new CustomEvent(EVENT_NAME, {
        detail: Object.assign({}, current),
        bubbles: true
      })
    );
  }

  window.duskReadProductContextSeed = parseSeed;
  window.duskPublishProductContext = publish;

  // Silent: nothing could have subscribed yet, and nothing has changed.
  const seed = parseSeed(document);
  if (seed) {
    current = seed;
    window.DUSK = window.DUSK || {};
    window.DUSK.productContext = Object.assign({}, current);
  }
})();
